/**
 * Admin o'ng tugma menyulari: foydalanuvchi, filial (test: menu.test.ts).
 * Ruxsatlar backend bilan bir xil: filialni owner o'zgartiradi; menejer owner/menejerni boshqarmaydi.
 */
import { Ban, Copy, Eye, FileText, Filter, KeyRound, LayoutDashboard, LockKeyhole, Pencil, Phone, ShieldOff, UserCheck, Users, UsersRound, Wallet } from "lucide-react";
import { compactMenu, SEPARATOR, type MenuEntry } from "@/components/ui/context-menu";
import type { Me } from "@/features/auth/types";
import type { Translate } from "@/features/drivers/menu";
import type { AdminUser, AuditEntry, Branch } from "./types";
import { profileItem, type ProfileAction } from "@/features/people/profileAction";

export interface UserMenuActions {
  navigate: (path: string) => void;
  copy: (text: string) => void;
  call: (phone: string) => void;
  edit: () => void;
  block: () => void;
  unblock: () => void;
  /** Yangi (vaqtinchalik) parol berish -- email yo'q, parolni unutganga shu */
  setPassword: () => void;
  reset2fa: () => void;
  profile?: ProfileAction;
  /** Ruxsatlar oynasi */
  access?: () => void;
}

/** Ruxsatlarni faqat owner belgilaydi va faqat filial menejeri uchun (backend: accounts/access.py) */
export function hasAccessSettings(u: Pick<AdminUser, "role">, me: Pick<Me, "role">): boolean {
  return me.role === "owner" && u.role === "branch_manager";
}

export function canManageUser(u: Pick<AdminUser, "role">, me: Pick<Me, "role">): boolean {
  return me.role === "owner" || (u.role !== "owner" && u.role !== "branch_manager");
}

export function userMenu(u: AdminUser, me: Pick<Me, "id" | "role">, a: UserMenuActions, t: Translate): MenuEntry[] {
  const manage = canManageUser(u, me);
  const self = u.id === me.id;
  const ownsDrivers = u.role === "recruiter" && u.drivers_count > 0;
  return compactMenu([
    profileItem(a.profile, u.id, t, { role: u.role }),
    SEPARATOR,
    { key: "copy-username", label: t("copyUsername"), icon: Copy, hint: u.username, onSelect: () => a.copy(u.username) },
    !!u.phone && { key: "call", label: t("call"), icon: Phone, hint: u.phone, onSelect: () => a.call(u.phone) },
    !!u.phone && { key: "copy-phone", label: t("copyPhone"), icon: Copy, onSelect: () => a.copy(u.phone) },
    SEPARATOR,
    ownsDrivers && { key: "drivers", label: t("user.showDrivers", { count: u.drivers_count }), icon: Users, onSelect: () => a.navigate(`/drivers?owner=${u.id}`) },
    SEPARATOR,
    manage && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
    !!a.access && hasAccessSettings(u, me) && { key: "access", label: t("user.access"), icon: KeyRound, onSelect: a.access },
    // O'zinikini -- profilda, joriy parol bilan (backend ham rad etadi)
    manage && !self && { key: "set-password", label: t("user.setPassword"), icon: LockKeyhole, onSelect: a.setPassword },
    // O'zinikini -- profilda, parol bilan (backend ham rad etadi)
    me.role === "owner" && !self && u.totp_enabled && { key: "reset-2fa", label: t("user.reset2fa"), icon: ShieldOff, onSelect: a.reset2fa },
    SEPARATOR,
    manage && u.is_blocked && { key: "unblock", label: t("user.unblock"), icon: UserCheck, onSelect: a.unblock },
    manage && !u.is_blocked && !self && { key: "block", label: t("user.block"), icon: Ban, danger: true, onSelect: a.block },
  ]);
}

export interface BranchMenuActions {
  navigate: (path: string) => void;
  openDashboard: () => void;
  openBudget: () => void;
  edit: () => void;
  profile?: ProfileAction;
}

export function branchMenu(b: Branch, me: Pick<Me, "role">, a: BranchMenuActions, t: Translate): MenuEntry[] {
  const owner = me.role === "owner";
  return compactMenu([
    owner && { key: "dashboard", label: t("branch.openDashboard"), icon: LayoutDashboard, onSelect: a.openDashboard },
    owner && { key: "budget", label: t("branch.openBudget"), icon: Wallet, onSelect: a.openBudget },
    { key: "users", label: t("branch.showUsers", { count: b.recruiters_count }), icon: UsersRound, onSelect: () => a.navigate(`/admin/users?branch=${b.id}`) },
    // Filialda bir nechta menejer bo'lishi mumkin: har biriga "Profilni ochish"
    ...b.managers.map((m) => profileItem(a.profile, m.id, t, { name: m.display_name, role: "branch_manager", key: `manager-profile-${m.id}` })),
    SEPARATOR,
    owner && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
  ]);
}

/** Audit yozuvidagi obyekt sahifasi (bor bo'lsa) */
export function auditObjectPath(model: string, objectId: string): string | null {
  if (!objectId) return null;
  const paths: Record<string, string> = { "drivers.driver": "/drivers/", "carriers.carrier": "/carriers/", "carriers.job": "/jobs/" };
  return paths[model] ? `${paths[model]}${objectId}` : null;
}

export interface AuditMenuActions {
  showDetails: () => void;
  filterBy: (param: "actor" | "model", value: string) => void;
  navigate: (path: string) => void;
  copy: (text: string) => void;
  profile?: ProfileAction;
}

export function auditMenu(e: AuditEntry, a: AuditMenuActions, t: Translate): MenuEntry[] {
  const path = e.link ?? auditObjectPath(e.model, e.object_id);
  return compactMenu([
    { key: "details", label: t("audit.showDetails"), icon: FileText, onSelect: a.showDetails },
    !!path && { key: "open", label: t("audit.openObject", { name: e.object_repr || e.object_id }), icon: Eye, onSelect: () => a.navigate(path as string) },
    SEPARATOR,
    profileItem(a.profile, e.actor?.id, t, { name: e.actor?.display_name, key: "actor-profile" }),
    !!e.actor && { key: "by-actor", label: t("audit.filterActor", { name: e.actor.display_name }), icon: Filter, onSelect: () => a.filterBy("actor", String(e.actor?.id)) },
    !!e.model && { key: "by-model", label: t("audit.filterModel"), icon: Filter, onSelect: () => a.filterBy("model", e.model) },
    SEPARATOR,
    {
      key: "copy",
      label: t("audit.copyDetails"),
      icon: Copy,
      onSelect: () => a.copy(JSON.stringify({ time: e.created_at, actor: e.actor_label, action: e.action, object: `${e.model}#${e.object_id}`, changes: e.changes, ip: e.ip, request_id: e.request_id }, null, 2)),
    },
    !!e.request_id && { key: "copy-request", label: t("audit.copyRequestId"), icon: Copy, hint: e.request_id.slice(0, 8), onSelect: () => a.copy(e.request_id) },
  ]);
}
